export const queryToJson = (queryString) => {
  if (!queryString) return {};

  // Remove leading '?' if present
  const cleanQuery = queryString.startsWith("?")
    ? queryString.slice(1)
    : queryString;

  return cleanQuery.split("&").reduce((params, param) => {
    const [key, value] = param.split("=");
    if (key) {
      // Try to parse JSON values, fallback to string
      try {
        params[key] = JSON.parse(decodeURIComponent(value));
      } catch {
        params[key] = decodeURIComponent(value || "");
      }
    }
    return params;
  }, {});
};

export const jsonToQuery = (json) => {
  if (!json || typeof json !== "object") return "";

  return Object.entries(json)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => {
      const encodedValue =
        typeof value === "object"
          ? encodeURIComponent(JSON.stringify(value))
          : encodeURIComponent(value);
      return `${encodeURIComponent(key)}=${encodedValue}`;
    })
    .join("&");
};

export const updateQueryParams = (params, replace = false) => {
  const currentParams = queryToJson(window.location.search);
  const newParams = { ...currentParams, ...params };
  const queryString = jsonToQuery(newParams);

  const newUrl = `${window.location.pathname}${queryString ? "?" + queryString : ""}`;

  if (replace) {
    window.history.replaceState({}, "", newUrl);
  } else {
    window.history.pushState({}, "", newUrl);
  }
};

// Flight search parametrelerini URL'den parse eden fonksiyon
export const parseFlightSearchFromQuery = (searchParams) => {
  const queryHelper = new QueryHelper(searchParams);

  // Tarih parse eden yardımcı fonksiyon
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  };

  // Şehir bilgisini parse eden fonksiyon
  const parseCity = (cityParam) => {
    if (!cityParam) return null;

    // Eğer JSON string ise parse et
    try {
      const parsed = JSON.parse(decodeURIComponent(cityParam));
      if (parsed.id && parsed.code && parsed.value) {
        return parsed;
      }
    } catch {}

    // Eğer basit string formatında ise (code-name şeklinde)
    const parts = cityParam.split("-");
    if (parts.length >= 2) {
      return {
        id: parts[0],
        code: parts[0],
        value: parts.slice(1).join("-"),
        cityName: parts[1],
        countryName: parts[2] || "",
      };
    }

    return null;
  };

  // Yolcu sayılarını parse et
  const parsePassengers = () => {
    const senior = parseInt(queryHelper.getString("senior", "0")) || 0;
    const elderly = parseInt(queryHelper.getString("elderly", "0")) || 0;
    return {
      adult: parseInt(queryHelper.getString("adult", "1")) || 1,
      child: parseInt(queryHelper.getString("child", "0")) || 0,
      infant: parseInt(queryHelper.getString("infant", "0")) || 0,
      student: parseInt(queryHelper.getString("student", "0")) || 0,
      senior: senior + elderly, // elderly değeri senior'a eklenir
    };
  };

  // Parse edilen data objesi
  const flightSearchData = {};

  // Tab tipini belirle (URL path'ından veya query'den)
  let activeTab = 0; // default: one-way

  // Önce query parametresinden kontrol et
  const tabFromQuery = queryHelper.getString("tab");
  if (tabFromQuery) {
    const tabNumber = parseInt(tabFromQuery);
    if ([0, 1, 2].includes(tabNumber)) {
      activeTab = tabNumber;
    }
  } else {
    // Query'de yoksa URL path'ından belirle
    const pathSegments =
      typeof window !== "undefined" ? window.location.pathname.split("/") : [];

    if (pathSegments.includes("one-way")) {
      activeTab = 0;
    } else if (pathSegments.includes("round-trip")) {
      activeTab = 1;
    } else if (pathSegments.includes("multi-city")) {
      activeTab = 2;
    }
  }

  flightSearchData.initialActiveTab = activeTab;

  // Şehir kodları (sadece kod olarak geçireceğiz, API'den detay bilgileri alınacak)
  const fromCityCode = queryHelper.getString("from");
  const toCityCode = queryHelper.getString("to");

  if (fromCityCode) {
    flightSearchData.initialDepartureCityCode = fromCityCode;
  }

  if (toCityCode) {
    flightSearchData.initialReturnCityCode = toCityCode;
  }

  // Tarih bilgileri
  const departureDate =
    queryHelper.getString("departureDate") || queryHelper.getString("depDate");
  const returnDate =
    queryHelper.getString("returnDate") || queryHelper.getString("retDate");

  if (departureDate) {
    flightSearchData.initialDepartureDate = parseDate(departureDate);
  }

  if (returnDate) {
    flightSearchData.initialReturnDate = parseDate(returnDate);
  }

  // Yolcu bilgileri - sadece toplam yolcu sayısı 0'dan büyükse ekle
  const passengers = parsePassengers();
  const totalPassengers = Object.values(passengers).reduce(
    (sum, count) => sum + count,
    0,
  );

  if (totalPassengers > 0) {
    flightSearchData.initialPassengers = passengers;
  }

  // Kabin sınıfı
  const cabinClass = queryHelper.getString("class");
  if (cabinClass && ["economy", "business", "first"].includes(cabinClass)) {
    flightSearchData.initialCabinClass = cabinClass;
  }

  // Direkt uçuş seçeneği - sadece checkbox'tan gelen directOnly (filtre paneli etkilemez)
  const directOnly = queryHelper.getBoolean("directOnly");
  if (directOnly) {
    flightSearchData.initialOnlyDirectFlights = true;
  }

  return flightSearchData;
};

/**
 * Hotel query parametrelerini URL'den parse eden fonksiyon
 * Format: ?check_in=2025-12-06&check_out=2025-12-09&adult_1=1&child_1=2&childage_1_1=3&childage_1_2=6
 * 
 * Next.js'te searchParams bir obje olarak gelir: { check_in: "2025-12-11", check_out: "2025-12-14", ... }
 */
export const parseHotelQuery = (searchParams) => {
  // Next.js'te searchParams zaten bir obje, direkt kullanabiliriz
  // Eğer URLSearchParams ise, obje formatına çevir
  let params = {};
  
  if (searchParams instanceof URLSearchParams) {
    // URLSearchParams ise, obje formatına çevir
    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }
  } else if (searchParams && typeof searchParams === 'object') {
    // Zaten obje formatında
    params = searchParams;
  }
  
  // Değerleri al (string array olabilir, ilk değeri al)
  const getParam = (key, defaultValue = '') => {
    const value = params[key];
    if (Array.isArray(value)) {
      return value[0] || defaultValue;
    }
    return value || defaultValue;
  };
  
  const check_in = getParam('check_in', '');
  const check_out = getParam('check_out', '');
  const adult_1 = parseInt(getParam('adult_1', '1'), 10) || 1;
  const child_1 = parseInt(getParam('child_1', '0'), 10) || 0;
  
  // childage_1_1, childage_1_2, ... formatında çocuk yaşlarını parse et
  const childAges = [];
  for (let i = 1; i <= child_1; i++) {
    const age = getParam(`childage_1_${i}`, '');
    if (age) {
      childAges.push(parseInt(age, 10));
    }
  }
  
  return {
    check_in,
    check_out,
    adult_1,
    child_1,
    childAges,
    // Detay sayfası mı yoksa landing sayfası mı?
    isDetailPage: !!(check_in && check_out),
  };
};

export class QueryHelper {
  constructor(searchParams) {
    this.searchParams = new URLSearchParams(searchParams.toString());
  }

  // String değer al
  getString(key, defaultValue = "") {
    return this.searchParams.get(key) || defaultValue;
  }

  // Boolean değer al
  getBoolean(key, defaultValue = false) {
    const value = this.searchParams.get(key);
    if (value === null) return defaultValue;
    return value === "true";
  }

  // Array değer al
  getArray(key, defaultValue = []) {
    const value = this.searchParams.get(key);
    if (!value) return defaultValue;
    return value.split(",");
  }

  // Range değer al (örn: "1000-2000" -> [1000, 2000])
  getRange(key, defaultValue = [0, 0]) {
    const value = this.searchParams.get(key);
    if (!value) return defaultValue;
    return value.split("-").map(Number);
  }

  // String değer ayarla
  setString(key, value) {
    if (value) {
      this.searchParams.set(key, value);
    } else {
      this.searchParams.delete(key);
    }
  }

  // Boolean değer ayarla
  setBoolean(key, value) {
    if (value) {
      this.searchParams.set(key, "true");
    } else {
      this.searchParams.delete(key);
    }
  }

  // Array değer ayarla
  setArray(key, values) {
    if (values && values.length > 0) {
      this.searchParams.set(key, values.join(","));
    } else {
      this.searchParams.delete(key);
    }
  }

  // Range değer ayarla
  setRange(key, [min, max]) {
    if (min !== undefined && max !== undefined) {
      this.searchParams.set(key, `${min}-${max}`);
    } else {
      this.searchParams.delete(key);
    }
  }

  // URL query string'ini döndür
  toString() {
    return this.searchParams.toString();
  }
}
